<template>
  <!-- Оценка заказа — только если заказ доставлен и отзыв не отправлен -->
  <div v-if="shouldShow && !ratingSent" class="order-rating">
    <h4 class="order-rating__title">Оцените качество заказа</h4>
    <div class="order-rating__stars">
      <UiStarRating v-model="rating" />
      <UTextarea
        v-model="comment"
        placeholder="Ваши пожелания"
        autoresize
        :rows="5"
      />
    </div>
    <UiKitButton
      color="warning"
      variant="solid"
      size="lg"
      radius="lg"
      full-width
      :loading="isSending"
      :disabled="!rating"
      @click="handleSendRating"
    >
      Оценить
    </UiKitButton>
  </div>

  <!-- Спасибо за отзыв -->
  <div v-if="ratingSent" class="order-rating-success">
    <div class="order-rating-success__msg">Спасибо за ваш отзыв!</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { consola } from 'consola'
import { shouldShowRating } from '@entities/order'
import type { Order } from '@entities/order'

const props = defineProps<{
  order: Order | null
  orderId?: string | number | null
  userId: string | null
}>()

const emit = defineEmits<{
  (e: 'ratingSent'): void
  (e: 'ratingError', error: string): void
}>()

// Reactive состояния
const rating = ref(0)
const comment = ref('')
const isSending = ref(false)
const ratingSent = ref(false)

/**
 * Проверяет, должен ли показываться блок оценки
 */
const shouldShow = computed(() => {
  return shouldShowRating(props.order, ratingSent.value)
})

/**
 * Отправляет оценку заказа
 */
async function handleSendRating(): Promise<void> {
  if (!props.orderId || !rating.value || isSending.value || !props.userId) {
    return
  }

  isSending.value = true

  try {
    // Импорт API модуля заказов (метод оценки определён в apiOrder)
    const { apiOrder } = await import('~/entities/order')

    await apiOrder.sendOrderRating({
      userId: Number(props.userId),
      orderId: Number(props.orderId),
      quality: rating.value,
      comment: comment.value,
    })

    ratingSent.value = true
    emit('ratingSent')

  } catch (error: any) {
    consola.error('Failed to send rating:', error)

    // Проверяем, является ли это ошибкой "Rating already sent"
    const status = error?.response?.status || error?.status || error?.statusCode
    const errorMessage = error?.response?._data?.error ||
                        error?.response?._data?.message ||
                        error?.data?.error ||
                        error?.data?.message ||
                        error?.message ||
                        ''

    const isRatingAlreadySent = status === 400 && (
      errorMessage.toLowerCase().includes('rating already sent') ||
      errorMessage.toLowerCase().includes('уже отправлен') ||
      errorMessage.toLowerCase().includes('уже оставлен') ||
      errorMessage.toLowerCase().includes('already sent') ||
      errorMessage === 'Rating already sent'
    )

    if (isRatingAlreadySent) {
      // Если отзыв уже отправлен, показываем сообщение "Спасибо за ваш отзыв!"
      ratingSent.value = true
      // Не показываем ошибку, так как отзыв уже был отправлен ранее
    } else {
      // Для других ошибок показываем стандартное сообщение
      emit('ratingError', 'Не удалось отправить оценку. Попробуйте позже.')
    }
  } finally {
    isSending.value = false
  }
}
</script>

<style scoped lang="scss">
.order-rating {
  @apply bg-hui-content2 rounded-hui-lg p-4 text-center;

  &__title {
    @apply font-semibold mb-2.5 text-lg text-hui-foreground;
  }

  &__stars {
    @apply grid gap-2 mb-3.5;

    .stars {
      @apply flex justify-center;
    }
  }
}

.order-rating-success {
  @apply text-center;

  &__msg {
    @apply text-base bg-hui-success/15 p-4 rounded-hui-lg text-hui-success-700 font-semibold;
  }
}
</style>